import { BorrowerOnboardingDetails, DocumentationRequiredType, OnboardingDetailsRole } from '@nemuru-eng/js-api-client';

import { CustomRequiredFile } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';

export type DocumentGroupKey = OnboardingDetailsRole | DocumentationRequiredType;

export const DOCUMENT_GROUP_ORDER: DocumentGroupKey[] = [
  OnboardingDetailsRole.BORROWER,
  OnboardingDetailsRole.CO_BORROWER,
  OnboardingDetailsRole.GUARANTOR,
  DocumentationRequiredType.LOAN,
  DocumentationRequiredType.MERCHANT,
  DocumentationRequiredType.LENDER,
];

const getParticipantFullName = (onboardingDetails: BorrowerOnboardingDetails): string | undefined => {
  const documentId = onboardingDetails.userDetails?.documentId;

  if (documentId?.fullName) {
    return `${documentId.fullName.firstName} ${documentId.fullName.lastName}`.trim() || undefined;
  }

  if (onboardingDetails.invitation?.fullName) {
    return (
      `${onboardingDetails.invitation.fullName.firstName} ${onboardingDetails.invitation.fullName.lastName}`.trim() ||
      undefined
    );
  }

  return undefined;
};

export const RequiredFilesByDocumentTypeGroupMap = {
  formatParticipantGroupTitleSuffix: (onboardingDetails: BorrowerOnboardingDetails | null): string | undefined => {
    if (!onboardingDetails) {
      return undefined;
    }

    const fullName = getParticipantFullName(onboardingDetails);
    const documentNumber = onboardingDetails.userDetails?.documentId?.documentNumber;

    if (!fullName || !documentNumber) {
      return undefined;
    }

    return ` (${fullName} - ${documentNumber})`;
  },

  getOnboardingDetailsRole: (
    requiredFile: CustomRequiredFile,
    onboardingDetails: BorrowerOnboardingDetails[],
  ): OnboardingDetailsRole | undefined => {
    if (requiredFile.type !== DocumentationRequiredType.BORROWER) {
      return undefined;
    }

    const matchingOnboardingDetails = onboardingDetails.find(
      (details) => 'borrowerId' in details && details.borrowerId === requiredFile.borrowerId,
    );

    return matchingOnboardingDetails?.role;
  },

  getDocumentGroupKey: (
    requiredFile: CustomRequiredFile,
    onboardingDetailsRole?: OnboardingDetailsRole,
  ): DocumentGroupKey => {
    if (requiredFile.type === DocumentationRequiredType.LOAN) {
      return DocumentationRequiredType.LOAN;
    }
    if (requiredFile.type === DocumentationRequiredType.BORROWER && onboardingDetailsRole) {
      return onboardingDetailsRole;
    }

    if (requiredFile.type === DocumentationRequiredType.MERCHANT) {
      return DocumentationRequiredType.MERCHANT;
    }

    if (requiredFile.type === DocumentationRequiredType.LENDER) {
      return DocumentationRequiredType.LENDER;
    }

    return OnboardingDetailsRole.BORROWER;
  },

  groupRequiredFilesByDocumentGroup: (
    files: CustomRequiredFile[],
    getOnboardingDetailsRole: (requiredFile: CustomRequiredFile) => OnboardingDetailsRole | undefined,
  ): { groupKey: DocumentGroupKey; files: CustomRequiredFile[] }[] => {
    const groupedFiles = new Map<DocumentGroupKey, CustomRequiredFile[]>();

    files.forEach((requiredFile) => {
      const groupKey = RequiredFilesByDocumentTypeGroupMap.getDocumentGroupKey(
        requiredFile,
        getOnboardingDetailsRole(requiredFile),
      );
      const currentGroupFiles = groupedFiles.get(groupKey) ?? [];

      groupedFiles.set(groupKey, [...currentGroupFiles, requiredFile]);
    });

    return DOCUMENT_GROUP_ORDER.flatMap((groupKey) => {
      const groupFiles = groupedFiles.get(groupKey);

      if (!groupFiles?.length) {
        return [];
      }

      return [{ groupKey, files: groupFiles }];
    });
  },

  getParticipantGroupTitleSuffix: (
    groupKey: DocumentGroupKey,
    getOnboardingDetails: (role: OnboardingDetailsRole) => BorrowerOnboardingDetails | null,
  ): string | undefined => {
    if (groupKey === DocumentationRequiredType.LOAN) {
      return undefined;
    }

    return RequiredFilesByDocumentTypeGroupMap.formatParticipantGroupTitleSuffix(
      getOnboardingDetails(groupKey as OnboardingDetailsRole),
    );
  },

  sortPendingGroupFiles: (files: CustomRequiredFile[]): CustomRequiredFile[] =>
    [...files].sort((a, b) => a.name.localeCompare(b.name)).sort((a, b) => (a.status < b.status ? 1 : -1)),

  sortUploadedGroupFiles: (files: CustomRequiredFile[]): CustomRequiredFile[] =>
    [...files].sort((a, b) => a.name.localeCompare(b.name)).sort((a, b) => (a.status < b.status ? -1 : 1)),
};
